/**
 * Period selector utilities — calculate date ranges from period presets.
 */

export const PERIODS = [
  { key: 'TODAY', label: 'Today' },
  { key: 'LAST_7_DAYS', label: 'Last 7 Days' },
  { key: 'THIS_MONTH', label: 'This Month' },
  { key: 'LAST_MONTH', label: 'Last Month' },
  { key: 'THIS_QUARTER', label: 'This Quarter' },
  { key: 'THIS_YEAR', label: 'This Year' },
];

export const getPeriodDates = (periodKey) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (periodKey) {
    case 'TODAY':
      return { from: today, to: today, label: today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) };
    case 'LAST_7_DAYS': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from, to: today, label: 'Last 7 Days' };
    }
    case 'THIS_MONTH': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from, to: today, label: today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) };
    }
    case 'LAST_MONTH': {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from, to, label: from.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) };
    }
    case 'THIS_QUARTER': {
      const q = Math.floor(today.getMonth() / 3);
      const from = new Date(today.getFullYear(), q * 3, 1);
      return { from, to: today, label: `Q${q + 1} ${today.getFullYear()}` };
    }
    case 'THIS_YEAR': {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from, to: today, label: `${today.getFullYear()}` };
    }
    default:
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today, label: 'This Month' };
  }
};

export const getComparisonLabel = (periodKey) => {
  const now = new Date();
  switch (periodKey) {
    case 'TODAY': return 'vs yesterday';
    case 'LAST_7_DAYS': return 'vs previous 7 days';
    case 'THIS_MONTH': {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `vs ${prev.toLocaleDateString('en-GB', { month: 'short' })}`;
    }
    case 'LAST_MONTH': {
      const prev = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      return `vs ${prev.toLocaleDateString('en-GB', { month: 'short' })}`;
    }
    case 'THIS_QUARTER': return 'vs last quarter';
    case 'THIS_YEAR': return 'vs last year';
    default: return '';
  }
};

// YYYY-MM-DD from the local date (toISOString would shift it to UTC and lose a day east of Greenwich).
export const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const shiftMonths = (d, months) => {
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), lastDay));
};

const shiftDays = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

/** The like-for-like earlier range for a period: the same days one period back (1–15 Oct → 1–15 Sep). */
export const getComparisonDates = (periodKey) => {
  const { from, to } = getPeriodDates(periodKey);
  switch (periodKey) {
    case 'TODAY': return { from: shiftDays(from, -1), to: shiftDays(to, -1) };
    case 'LAST_7_DAYS': return { from: shiftDays(from, -7), to: shiftDays(to, -7) };
    case 'THIS_QUARTER': return { from: shiftMonths(from, -3), to: shiftMonths(to, -3) };
    case 'THIS_YEAR': return { from: shiftMonths(from, -12), to: shiftMonths(to, -12) };
    case 'LAST_MONTH': return { from: shiftMonths(from, -1), to: new Date(from.getFullYear(), from.getMonth(), 0) };
    default: return { from: shiftMonths(from, -1), to: shiftMonths(to, -1) };
  }
};
